<template>
    <b-card
        :title="translations[`${mode}ProductTitle`][this.lang]"
        :loading="loading"
        :error-message="errorMessage"
        @cancel="closeForm"
        @submit="submitProduct"
        :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
        :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
    >
        <b-text-field
            v-model="product.name[productLang]"
            :label="translations.productNameModal[lang]"
            :success="productLang === 'el' ? success.name : false"
            :error-messages="error.name"
            @focus="error.name = ''"
            @blur="validateName"
        >
            <template v-slot:append>
                <b-lang-menu v-model="productLang" type="inner"></b-lang-menu>
            </template>
        </b-text-field>

        <b-textarea
            v-model="product.description[descriptionLang]"
            :label="translations.productDescriptionModal[lang]"
            :success="descriptionLang === 'el' ? success.description : false"
            :error-messages="error.description"
            @focus="error.description = ''"
            @blur="validateDescription"
        >
            <template v-slot:append>
                <b-lang-menu
                    v-model="descriptionLang"
                    type="inner"
                ></b-lang-menu>
            </template>
        </b-textarea>

        <b-select
            :items="productTypes"
            :item-text="`name[${lang}]`"
            item-value="subscription_product_type_id"
            v-model="product.subscription_product_type_id"
            :label="translations.productType[lang]"
        ></b-select>
        <v-row v-if="selectedType.system == 'credit'">
            <v-col cols="12" sm="6">
                <b-select
                    :items="credits"
                    :item-text="`name[${lang}]`"
                    item-value="credit_id"
                    v-model="selectedCreditId"
                    :label="translations.creditType[lang]"
                ></b-select>
            </v-col>
            <v-col cols="12" sm="6" v-if="selectedCreditId">
                <b-select
                    :items="creditOptions"
                    :item-text="`option[${lang}]`"
                    item-value="credit_option_id"
                    v-model="product.credit_option_id"
                    :label="translations.creditOption[lang]"
                    :disabled="!selectedCreditId"
                ></b-select>
            </v-col>
        </v-row>
        <v-autocomplete
            v-else-if="product.subscription_product_type_id"
            :items="[...variants, ...selectedVariantsData]"
            item-value="subscription_product_variant_id"
            v-model="product.variant_ids"
            :label="translations.variants[lang]"
            menu-props="offsetY"
            class="mt-3"
            color="secondary"
            multiple
            small-chips
            outlined
            dense
            chips
            open-on-clear
            clearable
            hide-details="auto"
            :search-input.sync="search"
            :loading="loading"
            :disable-lookup="true"
            item-disabled="disable"
        >
            <template v-slot:selection="data">
                <div v-if="product.subscription_product_variant_ids">
                    <v-chip
                        v-if="
                            product.subscription_product_variant_ids.length < 5
                        "
                        v-bind="data.attrs"
                        close
                        small
                        class="mt-1"
                    >
                        {{ data.item.name[lang] }}
                    </v-chip>
                    <div v-else-if="data.index === 0">
                        {{
                            `${product.subscription_product_variant_ids.length} ` +
                                translations.variantsSelected[lang]
                        }}
                    </div>
                </div>
            </template>
            <template v-slot:item="{ item }">
                <v-list-item-content>
                    <v-list-item-title class=" d-flex justify-space-between">
                        <span v-if="item.name">{{ item.name[lang] }}</span>
                        <span v-if="item.price" class="ml-2 text--secondary"
                            >{{ translations.price[lang] }}: {{ item.price }}
                        </span>
                    </v-list-item-title>
                    <v-list-item-subtitle class="">
                        <span v-if="item.description">{{
                            item.description[lang]
                        }}</span>
                    </v-list-item-subtitle>
                </v-list-item-content>
            </template>
            <template v-slot:append-item>
                <div v-intersect="endIntersectVariants"></div>
                <v-list-item v-if="loading">
                    <v-list-item-action class="ml-1">
                        <v-row class="d-flex align-center">
                            <v-btn
                                class="text-capitalize"
                                depressed
                                fab
                                small
                                :loading="true"
                                style="background: transparent"
                            >
                            </v-btn>
                            <span class="pa-2">{{
                                translations.loadMore[lang]
                            }}</span>
                        </v-row>
                    </v-list-item-action>
                </v-list-item>
            </template>
        </v-autocomplete>
        <v-autocomplete
            class="mt-3"
            :items="companies"
            :item-text="'name'"
            item-value="store_id"
            v-model="selectedStoreIds"
            multiple
            :label="translations.companies[lang]"
            menu-props="offsetY"
            color="secondary"
            item-color="secondary"
            outlined
            dense
            open-on-clear
            hide-details="auto"
            chips
            deletable-chips
            small-chip
            clearable
            :search-input.sync="search"
            :loading="loading"
            :disable-lookup="true"
            item-disabled="disable"
        >
            <template v-slot:prepend-item>
                <v-list-item
                    ripple
                    style="cursor: pointer"
                    @click="toggleSelectAllStores"
                >
                    <v-checkbox
                        @click.stop.prevent="toggleSelectAllStores"
                        v-model="allStoresAreSelected"
                        readonly
                        class="ml-0 mt-0 mb-n3 transition-all"
                        color="secondary"
                    >
                    </v-checkbox>
                    <v-list-item-action class="ml-6">
                        {{ translations.all[lang] }}
                    </v-list-item-action>
                </v-list-item>
            </template>
            <template v-slot:selection="data">
                <v-chip
                    v-if="selectedStoreIds.length < 5"
                    v-bind="data.attrs"
                    close
                    small
                    class="mt-1"
                    @click:close="removeSelectedStore(data)"
                >
                    {{ data.item.name }}
                </v-chip>
                <div v-else-if="data.index === 0">
                    {{
                        (allStoresAreSelected && translations.all[lang]) ||
                            `${selectedStoreIds.length} ` +
                                translations.storesSelected[lang]
                    }}
                </div>
            </template>

            <template v-slot:item="{ item }">
                <span style="position:absolute;">{{
                    item.name.replace("Company: ", "")
                }}</span>
            </template>
        </v-autocomplete>

        <v-card outlined class="mt-3">
            <v-card-title class="subtitle-1 font-weight-medium">
                {{ translations.image[lang] }}
            </v-card-title>
            <v-row no-gutters justify="space-between" class="pa-5">
                <v-col cols="6">
                    <v-img
                        v-if="newImage || product.image_url"
                        :src="newImage || product.image_url"
                    ></v-img>
                </v-col>
                <v-col cols="5" class="mr-3">
                    <v-file-input
                        color="secondary"
                        outlined
                        dense
                        hide-details
                        v-model="imageInput"
                        @change="onFileSelected"
                    ></v-file-input>
                </v-col>
            </v-row>
        </v-card>

        <v-checkbox
            v-model="product.visibility"
            color="secondary"
            class="mt-3 pt-0"
            hide-details="auto"
        >
            <template v-slot:label>
                <h4 class="secondary--text">
                    {{ translations.publish[lang] }}
                </h4>
            </template>
        </v-checkbox>
    </b-card>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";

import validators from "@/utils/validators/loyaltyPanel/product";
import translations from "@/utils/translations/general/settings/roadcubeMarket";

import debounce from "lodash/debounce";
import { cloneDeep } from "lodash";

export default {
    name: "SubscriptionProductForm",

    props: {
        mode: String
    },

    mixins: [validators, translations],

    data() {
        return {
            productLang: this.$route.params.lang,
            changeNameElAuto: true,
            changeNameItAuto: true,
            changeNameEnAuto: true,
            descriptionLang: this.$route.params.lang,
            changeDescriptionElAuto: true,
            changeDescriptionItAuto: true,
            changeDescriptionEnAuto: true,
            imageFile: null,
            search: "",
            selectedStoreIds: [],
            selectedCreditId: null,
            variantsPage: 1,
            variantsSearch: "",
            newImage: null,
            imageInput: null
        };
    },

    computed: {
        ...mapState([
            "loading",
            "errorMessage",
            "resetSuccess",
            "resetValidation",
            "userStores"
        ]),
        ...mapState("general/settings/roadcubeMarket/subscriptionProducts", [
            "product",
            "productTypes",
            "credits",
            "creditOptions"
        ]),
        ...mapState("general/settings/roadcubeMarket/variants", [
            "variants",
            "variantsPagination"
        ]),

        lang() {
            return this.$route.params.lang;
        },

        companies() {
            return (
                this.userStores?.filter(e => e.name?.includes("Company: ")) ||
                []
            );
        },

        allStoresAreSelected() {
            return this.companies?.length == this.selectedStoreIds.length;
        },

        selectedType() {
            return (
                this.productTypes?.find(
                    x =>
                        x.subscription_product_type_id ==
                        this.product.subscription_product_type_id
                ) || {}
            );
        },
        variantsQuery() {
            if (this.variantsSearch) {
                return `?q=${this.variantsSearch}&page=${this.variantsPage}`;
            }

            return {
                q: `?page=${this.variantsPage}`,
                page: this.variantsPage || 1
            };
        },

        selectedVariantsData() {
            return Object.keys(this.product.variants).filter(
                e =>
                    !this.variants.find(
                        x =>
                            x.subscription_product_variant_id ==
                            e.subscription_product_variant_id
                    )
            );
        }
    },

    methods: {
        ...mapMutations(["setResetSuccess", "setResetValidation"]),
        ...mapMutations(
            "general/settings/roadcubeMarket/subscriptionProducts",
            ["setShowImageUpload"]
        ),
        ...mapActions(["getUserStores"]),
        ...mapActions("general/settings/roadcubeMarket/variants", [
            "getVariants"
        ]),
        ...mapActions("general/settings/roadcubeMarket/subscriptionProducts", [
            "create",
            "update",
            "getProducts",
            "getProductTypes",
            "uploadImage",
            "getCredits",
            "getCreditOptions"
        ]),

        onFileSelected(event) {
            if (event) {
                this.imageFile = event;
                const reader = new FileReader();
                reader.readAsDataURL(this.imageFile);
                // this.product.image_url = null;
                reader.onload = e => {
                    this.newImage = e.target.result;
                };
            } else {
                this.product.image_url = "/images/productDefault.png";
            }
        },
        toggleSelectAllStores() {
            if (this.allStoresAreSelected) {
                this.selectedStoreIds.forEach((item, index) => {
                    this.selectedStoreIds.splice(
                        index,
                        this.selectedStoreIds.length
                    );
                });
            } else {
                this.companies.forEach(item => {
                    if (!this.selectedStoreIds.includes(item.store_id)) {
                        this.selectedStoreIds.push(item.store_id);
                    }
                });
            }
        },
        removeSelectedStore(data) {
            this.selectedStoreIds.splice(data.index, 1);
        },

        async submitProduct() {
            let product = cloneDeep(this.product);
            if (this.mode == "create") {
                product.image_url =
                    "https://www.mountaingoatsoftware.com/uploads/blog/2016-09-06-what-is-a-product.png";
                product.subscription_product_id = undefined;
            }
            product.store_ids = this.allStoresAreSelected
                ? []
                : this.selectedStoreIds;
            if (this.selectedType.system == "credit") {
                product.subscription_product_variant_ids = undefined;
            } else {
                product.credit_option_id = undefined;
            }
            let res = await this[this.mode](product);
            if (res?.data?.subscription_product_id) {
                product = res.data;
                if (this.imageFile) {
                    let imgProduct = await this.uploadImage({
                        product,
                        image: this.imageFile
                    });
                    product.image_url =
                        imgProduct.image_url ||
                        "https://www.mountaingoatsoftware.com/uploads/blog/2016-09-06-what-is-a-product.png";
                }
                await this.getProducts();
                this.closeForm();
            }
        },

        closeForm() {
            this.resetForm();
            this.$emit("cancel");
        },

        resetForm() {
            this.changeNameElAuto = true;
            this.changeNameItAuto = true;
            this.changeNameEnAuto = true;
            this.changeDescriptionElAuto = true;
            this.changeDescriptionItAuto = true;
            this.changeDescriptionEnAuto = true;
            this.imageFile = null;
            this.newImage = null;
            this.imageInput = null;
            this.search = "";
            this.selectedStoreIds = [];
            this.product.subscription_product_variant_ids = [];
        },
        endIntersectVariants(entries, observer, isIntersecting) {
            if (
                isIntersecting &&
                this.variants &&
                this.variants.length < this.variantsPagination.total
            ) {
                this.loadMoreVariants();
            }
        },
        async loadMoreVariants() {
            this.variantsPage++;
            await this.getVariants(this.variantsQuery);
        }
    },

    watch: {
        ["product.subscription_product_id"]: {
            handler(val) {
                this.resetForm();
                if (this.product.store_ids?.length) {
                    this.selectedStoreIds = [...this.product.store_ids];
                } else {
                    this.toggleSelectAllStores();
                }
                if (this.product.subscription_product_variant_ids?.length) {
                    this.selectedStoreIds = [...this.product.store_ids];
                }
            },
            deep: true
        },

        ["$route.params.lang"](val) {
            this.productLang = val;
            this.descriptionLang = val;
        },

        ["product.name.en"](val) {
            if (this.lang == "en") {
                if (this.changeNameElAuto) {
                    this.product.name.el = val;
                }

                if (this.changeNameItAuto) {
                    this.product.name.it = val;
                }

                this.changeNameEnAuto = false;
            } else if (val != this.product.name[this.lang]) {
                this.changeNameEnAuto = false;
            }
        },

        ["product.name.el"](val) {
            if (this.lang == "el") {
                if (this.changeNameEnAuto) {
                    this.product.name.en = val;
                }

                if (this.changeNameItAuto) {
                    this.product.name.it = val;
                }

                this.changeNameElAuto = false;
            } else if (val != this.product.name[this.lang]) {
                this.changeNameElAuto = false;
            }
        },

        ["product.name.it"](val) {
            if (this.lang == "it") {
                if (this.changeNameElAuto) {
                    this.product.name.el = val;
                }

                if (this.changeNameEnAuto) {
                    this.product.name.en = val;
                }

                this.changeNameItAuto = false;
            } else if (val != this.product.name[this.lang]) {
                this.changeNameItAuto = false;
            }
        },

        ["product.description.en"](val) {
            if (this.lang == "en") {
                if (this.changeDescriptionElAuto) {
                    this.product.description.el = val;
                }

                if (this.changeDescriptionItAuto) {
                    this.product.description.it = val;
                }

                this.changeDescriptionEnAuto = false;
            } else if (val != this.product.description[this.lang]) {
                this.changeDescriptionEnAuto = false;
            }
        },

        ["product.description.el"](val) {
            if (this.lang == "el") {
                if (this.changeDescriptionEnAuto) {
                    this.product.description.en = val;
                }

                if (this.changeDescriptionItAuto) {
                    this.product.description.it = val;
                }

                this.changeDescriptionElAuto = false;
            } else if (val != this.product.description[this.lang]) {
                this.changeDescriptionElAuto = false;
            }
        },

        ["product.description.it"](val) {
            if (this.lang == "it") {
                if (this.changeDescriptionElAuto) {
                    this.product.description.el = val;
                }

                if (this.changeDescriptionEnAuto) {
                    this.product.description.en = val;
                }

                this.changeDescriptionItAuto = false;
            } else if (val != this.product.description[this.lang]) {
                this.changeDescriptionItAuto = false;
            }
        },

        ["product.subscription_product_type_id"](val) {
            if (this.lang == "it") {
                if (this.changeDescriptionElAuto) {
                    this.product.description.el = val;
                }

                if (this.changeDescriptionEnAuto) {
                    this.product.description.en = val;
                }

                this.changeDescriptionItAuto = false;
            } else if (val != this.product.description[this.lang]) {
                this.changeDescriptionItAuto = false;
            }
        }
    },
    created() {},

    mounted() {
        if (!this.credits?.length) {
            this.getCredits();
        }
        if (!this.creditOptions?.length) {
            this.getCreditOptions();
        }
        if (!this.variants?.length) {
            this.getVariants(this.variantsQuery);
        }
    }
};
</script>
